// aiFeedConstants.js — Ai feed preference keys (client)

export const AI_FEED_SETTING_KEY = 'orbitalAiFeed';

export const AI_FEED_CATEGORIES = Object.freeze({
  RECORD_UPDATES: 'record_updates',
  TASKS_AND_MISSIONS: 'tasks_and_missions',
  MENTIONS: 'mentions',
  AUTOMATION_ACTIVITY: 'automation_activity',
  BASE_ALERTS: 'base_alerts',
  PROACTIVE_BRIEFINGS: 'proactive_briefings',
  WATCH_QUERIES: 'watch_queries',
  CAPTURE_AND_CLIP: 'capture_and_clip',
  SOCIAL_SHARES: 'social_shares',
});

export const AI_FEED_CATEGORY_OPTIONS = Object.freeze([
  {
    key: AI_FEED_CATEGORIES.RECORD_UPDATES,
    label: 'Include record updates',
    description: 'Includes changes to records you own, are assigned to, or follow.',
  },
  {
    key: AI_FEED_CATEGORIES.TASKS_AND_MISSIONS,
    label: 'Include task updates',
    description: 'Includes changes to tasks you own, are assigned to, or follow.',
  },
  {
    key: AI_FEED_CATEGORIES.MENTIONS,
    label: 'Mentions & replies',
    description: '@mentions and thread replies involving you.',
  },
  {
    key: AI_FEED_CATEGORIES.AUTOMATION_ACTIVITY,
    label: 'Automation activity',
    description: 'When your automation rules run.',
  },
  {
    key: AI_FEED_CATEGORIES.BASE_ALERTS,
    label: 'Base & billing alerts',
    description: 'Plan limits and admin announcements.',
  },
  {
    key: AI_FEED_CATEGORIES.PROACTIVE_BRIEFINGS,
    label: 'Daily briefings',
    description: 'Scheduled summaries of what is on your plate.',
  },
  {
    key: AI_FEED_CATEGORIES.WATCH_QUERIES,
    label: 'Watch alerts',
    description: 'When a saved watch query matches records.',
  },
  {
    key: AI_FEED_CATEGORIES.CAPTURE_AND_CLIP,
    label: 'Capture & Clipper receipts',
    description: 'Confirmations when you save via Capture or Clipper.',
  },
  {
    key: AI_FEED_CATEGORIES.SOCIAL_SHARES,
    label: 'Shares, DMs & handoffs',
    description: 'Records, notes, and DMs teammates send you from Ai.',
  },
]);

export const DEFAULT_AI_FEED_PREFERENCES = Object.freeze({
  enabled: true,
  directMessagesInFeed: true,
  categories: Object.values(AI_FEED_CATEGORIES).reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {}),
});

export function resolveAiFeedPreferences(userSettings, baseKey) {
  const cleanBaseKey = String(baseKey || '').trim();
  const feedRoot = userSettings?.[AI_FEED_SETTING_KEY];
  const byBase = feedRoot?.byBase && typeof feedRoot.byBase === 'object'
    ? feedRoot.byBase
    : {};
  const baseEntry = byBase?.[cleanBaseKey] && typeof byBase[cleanBaseKey] === 'object'
    ? byBase[cleanBaseKey]
    : {};

  return {
    enabled: baseEntry?.enabled !== false,
    directMessagesInFeed: baseEntry?.directMessagesInFeed !== false,
    categories: {
      ...DEFAULT_AI_FEED_PREFERENCES.categories,
      ...(baseEntry?.categories && typeof baseEntry.categories === 'object'
        ? baseEntry.categories
        : {}),
    },
  };
}

export function buildAiFeedSettingsPatch({
  userSettings,
  baseKey,
  patch = {},
}) {
  const cleanBaseKey = String(baseKey || '').trim();
  if (!cleanBaseKey) return null;

  const current = resolveAiFeedPreferences(userSettings, cleanBaseKey);
  const feedRoot = userSettings?.[AI_FEED_SETTING_KEY] && typeof userSettings[AI_FEED_SETTING_KEY] === 'object'
    ? userSettings[AI_FEED_SETTING_KEY]
    : {};
  const byBase = feedRoot?.byBase && typeof feedRoot.byBase === 'object'
    ? { ...feedRoot.byBase }
    : {};

  byBase[cleanBaseKey] = {
    ...current,
    ...patch,
    categories: {
      ...current.categories,
      ...(patch?.categories && typeof patch.categories === 'object' ? patch.categories : {}),
    },
  };

  return {
    [AI_FEED_SETTING_KEY]: {
      ...feedRoot,
      byBase,
    },
  };
}
